/**
 * STATUS PIE CHART
 *
 * Donut chart visualization for status distribution.
 * Shows execution status breakdown (Pass, Fail, Blocked, etc.) with
 * a center label showing total count.
 *
 * Uses Recharts PieChart with inner radius for donut style.
 * Colors use Atlaskit design tokens for theme compatibility.
 */

import React, { useMemo } from 'react'
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts'
import { token } from '@atlaskit/tokens'

// Status color mapping using Atlaskit design tokens
// Falls back to hex values for non-token environments
const STATUS_COLORS = {
  'Pass': token('color.background.success.bold', '#22A06B'),
  'Passed': token('color.background.success.bold', '#22A06B'),
  'Fail': token('color.background.danger.bold', '#DE350B'),
  'Failed': token('color.background.danger.bold', '#DE350B'),
  'Blocked': token('color.icon.warning', '#E06C00'),
  'Block': token('color.icon.warning', '#E06C00'),
  'In Progress': token('color.background.information.bold', '#0065FF'),
  'Not Run': token('color.background.neutral', '#DFE1E6'),
  'Skip': token('color.background.neutral', '#DFE1E6'),
  'Skipped': token('color.background.neutral', '#DFE1E6'),
  'Query': token('color.background.information', '#DEEBFF'),
  'Exclude': token('color.background.neutral.subtle', '#F4F5F7'),
  'Excluded': token('color.background.neutral.subtle', '#F4F5F7')
}

// Accent colors for dynamic/custom statuses not in the predefined list
const ACCENT_COLORS = [
  '#6554C0', // Purple
  '#00B8D9', // Teal
  '#FF5630', // Red-orange
  '#36B37E', // Green
  '#FFAB00', // Yellow
  '#403294', // Dark purple
  '#00A3BF', // Cyan
  '#BF2600'  // Dark red
]

/**
 * Get color for a status name
 * Uses predefined colors for known statuses, accent colors for custom statuses
 *
 * @param {string} statusName - Name of the status
 * @param {number} index - Index in the data array (for accent color selection)
 * @returns {string} Color value
 */
const getStatusColor = (statusName, index) => {
  // Check for exact match first
  if (STATUS_COLORS[statusName]) {
    return STATUS_COLORS[statusName]
  }

  // Check for case-insensitive match
  const normalizedName = statusName.toLowerCase()
  for (const [key, color] of Object.entries(STATUS_COLORS)) {
    if (key.toLowerCase() === normalizedName) {
      return color
    }
  }

  // Fall back to accent colors for custom statuses
  return ACCENT_COLORS[index % ACCENT_COLORS.length]
}

/**
 * Custom tooltip component for the pie chart
 */
const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) {
    return null
  }

  const data = payload[0].payload
  const percentage = ((data.value / data.total) * 100).toFixed(1)

  return (
    <div
      style={{
        backgroundColor: token('elevation.surface.overlay', '#FFFFFF'),
        border: `1px solid ${token('color.border', '#DFE1E6')}`,
        borderRadius: '4px',
        padding: '8px 12px',
        boxShadow: token('elevation.shadow.overlay', '0 4px 8px rgba(9, 30, 66, 0.25)')
      }}
    >
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        <div
          style={{
            width: '12px',
            height: '12px',
            borderRadius: '2px',
            backgroundColor: data.color
          }}
        />
        <span style={{
          fontWeight: 500,
          color: token('color.text', '#172B4D')
        }}>
          {data.name}
        </span>
      </div>
      <div style={{
        marginTop: '4px',
        color: token('color.text.subtle', '#6B778C'),
        fontSize: '12px'
      }}>
        {data.value} ({percentage}%)
      </div>
    </div>
  )
}

/**
 * Custom legend component
 */
const CustomLegend = ({ payload }) => {
  if (!payload || !payload.length) {
    return null
  }

  return (
    <div style={{
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'center',
      gap: '12px',
      marginTop: '16px'
    }}>
      {payload.map((entry, index) => (
        <div
          key={`legend-${index}`}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}
        >
          <div
            style={{
              width: '10px',
              height: '10px',
              borderRadius: '2px',
              backgroundColor: entry.color
            }}
          />
          <span style={{
            fontSize: '12px',
            color: token('color.text', '#172B4D')
          }}>
            {entry.value}
          </span>
          <span style={{
            fontSize: '12px',
            color: token('color.text.subtle', '#6B778C')
          }}>
            ({entry.payload.value})
          </span>
        </div>
      ))}
    </div>
  )
}

/**
 * Center label component showing total count
 */
const CenterLabel = ({ total, label }) => {
  return (
    <div style={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      textAlign: 'center',
      pointerEvents: 'none'
    }}>
      <div style={{
        fontSize: '28px',
        fontWeight: 600,
        color: token('color.text', '#172B4D'),
        lineHeight: 1.2
      }}>
        {total}
      </div>
      <div style={{
        fontSize: '12px',
        color: token('color.text.subtle', '#6B778C'),
        marginTop: '2px'
      }}>
        {label}
      </div>
    </div>
  )
}

/**
 * StatusPieChart Component
 *
 * @param {object} props
 * @param {object} props.statusDistribution - Map of status name to count, e.g., {"Pass": 50, "Fail": 5}
 * @param {number} props.width - Chart width (default: 100%)
 * @param {number} props.height - Chart height (default: 280)
 * @param {string} props.centerLabel - Label text below the total count (default: "Total")
 */
const StatusPieChart = ({
  statusDistribution = {},
  width = '100%',
  height = 280,
  centerLabel = 'Total',
  // Optional: receives the clicked segment's status name. When provided, the
  // pie is clickable and segments render with a pointer cursor.
  onSegmentClick
}) => {
  // Transform statusDistribution object into array format for Recharts
  const chartData = useMemo(() => {
    const entries = Object.entries(statusDistribution)
    const total = entries.reduce((sum, [, count]) => sum + count, 0)

    return entries
      .filter(([, count]) => count > 0) // Only include statuses with counts > 0
      .map(([name, value], index) => ({
        name,
        value,
        total,
        color: getStatusColor(name, index)
      }))
      // Sort by value descending for better visual hierarchy
      .sort((a, b) => b.value - a.value)
  }, [statusDistribution])

  // Calculate total executions
  const total = useMemo(() => {
    return chartData.reduce((sum, item) => sum + item.value, 0)
  }, [chartData])

  // Handle empty state
  if (chartData.length === 0 || total === 0) {
    return (
      <div
        style={{
          width,
          height,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          color: token('color.text.subtle', '#6B778C')
        }}
      >
        <div style={{ fontSize: '14px' }}>No execution data</div>
      </div>
    )
  }

  return (
    <div style={{ width, height }}>
      {/* Chart area — its own positioning context so CenterLabel centers on the donut hole,
          not on the outer container (which also reserves space for the legend below). */}
      <div style={{ position: 'relative', width: '100%', height: height - 60 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius="60%"
              outerRadius="85%"
              paddingAngle={2}
              dataKey="value"
              stroke="none"
              onClick={(entry) => {
                // Recharts passes the clicked payload as the first arg.
                if (onSegmentClick && entry?.name) onSegmentClick(entry.name)
              }}
              style={onSegmentClick ? { cursor: 'pointer' } : undefined}
            >
              {chartData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.color}
                />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>

        {/* Center label — absolutely positioned within the chart area above */}
        <CenterLabel total={total} label={centerLabel} />
      </div>

      {/* Legend — flows below the chart area */}
      <CustomLegend
        payload={chartData.map(item => ({
          value: item.name,
          color: item.color,
          payload: item
        }))}
      />
    </div>
  )
}

export default StatusPieChart
